import {
  all, call, select, takeLatest, put
} from 'redux-saga/effects';

import { actions as chatTicketActions } from 'react/api/actions/chatTicketActions';
import { actions as chatActions } from 'react/api/actions/chat';
import { actions as ticketHubActions } from 'react/api/actions/ticketHub';
import { actions as toastAlertsActions } from 'react/api/actions/toastAlerts';
import { parseMessageFromReassignedTicket } from 'react/utils/chatUtils';

import defaultFetch from './fetch/defaultFetch';

const {
  toggleFinishCallModal,
  toggleReassignCallModal,
  updateTicketResolutions,
  updateActiveTicket,
  submitFinishCall,
  submitReassignCall,
  submitAcceptCall,
  seeTicketInWindow
} = chatTicketActions;

const {
  removeToast
} = toastAlertsActions;

const { addNewChatWindow, removeChatWindow } = chatActions;
const {
  updateTicketStatus: updateStatusInTicketHub,
  updateTicketAccepted: acceptTicketInTicketHub
} = ticketHubActions;

function hasRequestErrors(message) {
  return message?.includes('Request failed');
}

/** Fetch tickets resolutions */
function* fetchAndUpdateTicketResolutions() {
  const url = '/TicketResolution/restrictions';
  const body = {
    sort: [],
    criteriaChain: []
  };

  const response = yield call(defaultFetch, url, body, 'POST');

  if (!response.error) {
    yield put({
      type: updateTicketResolutions,
      payload: response || []
    });
  }
}

function* toggleFinishCallModalSaga() {
  const { chatTicketActions: chatTicketActionsState } = yield select();
  const isOpen = chatTicketActionsState.getIn(['modalFinishCallIsOpen']);

  if (isOpen) {
    yield fetchAndUpdateTicketResolutions();
  }
}

function* watchToggleFinishCallModal() {
  yield takeLatest(toggleFinishCallModal, toggleFinishCallModalSaga);
}

/** Submit finish call */
function* submitFinishCallSaga(action) {
  const { payload } = action;

  const response = yield Monet.Session.Chat.Service.closeTicket(payload);

  if (!hasRequestErrors(response?.message)) {
    yield put({
      type: toggleFinishCallModal
    });
  } else {
    new Greenmile.Components
      .AlertModal('An error ocurred when try finish the ticket')
      .show();
  }
}

function* watchSubmitFinishCall() {
  yield takeLatest(submitFinishCall, submitFinishCallSaga);
}

/** Submit reasign call */
function* fetchTicketTypeUsersAndGroups(ticketTypeId) {
  const criteria = {
    filters: [
      'standardUsersCall.id',
      'standardGroupsCall.id'
    ],
    firstResult: 0,
    maxResults: 51
  };
  const url = `/TicketType/restrictions?criteria=${JSON.stringify(criteria)}`;
  const encodedUrl = encodeURI(url);

  const body = {
    sort: [],
    criteriaChain: [
      {
        and: [
          {
            attr: 'id',
            eq: ticketTypeId
          }
        ]
      }
    ]
  };

  const response = yield call(defaultFetch, encodedUrl, body, 'POST');

  const { user } = yield select();
  const userId = user.get('id');

  const availableUsersIds = response[0]
    ?.standardUsersCall
    ?.map((item) => item.id)
    ?.filter((item) => item !== userId);

  const availableGroupsIds = response[0]?.standardGroupsCall?.map((item) => item.id);

  const data = {
    availableGroupsIds,
    availableUsersIds
  };

  return data;
}

function* fetchAndUpdateAvailableUsersAndGroups(ticketTypeId) {
  const availableUsersAndGroups = yield call(
    fetchTicketTypeUsersAndGroups,
    ticketTypeId
  );

  yield put({
    type: updateActiveTicket,
    payload: availableUsersAndGroups
  });
}

function* toggleReassignCallModalSaga({ payload: activeTicket }) {
  if (activeTicket) {
    const ticketTypeId = activeTicket.get('ticketTypeId');
    yield fetchAndUpdateAvailableUsersAndGroups(ticketTypeId);
  }
}

function* watchToggleReassignCallModal() {
  yield takeLatest(toggleReassignCallModal, toggleReassignCallModalSaga);
}

function* submitReassignCallSaga(action) {
  const { payload } = action;
  const { ticketId } = payload;

  const response = yield Monet.Session.Chat.Service.reassignTicket(payload);

  if (!hasRequestErrors(response?.message)) {
    yield put({
      type: toggleReassignCallModal
    });

    yield put({
      type: updateStatusInTicketHub,
      payload: {
        ticketId,
        status: 'RE_ASSIGNED'
      }
    });

    yield put({
      type: removeChatWindow,
      payload: {
        chatId: ticketId
      }
    });
  } else {
    new Greenmile.Components
      .AlertModal('Happen a error when try reasign the ticket')
      .show();
  }
}

function* watchSubmitReassignCall() {
  yield takeLatest(submitReassignCall, submitReassignCallSaga);
}

/** Submit accept call */
function* getTicketOldMessages(ticketId) {
  const oldMessagesOfTheTicket = yield Monet.Session.Chat.Service.getOldMessages(ticketId);

  try {
    const parsedMessages = oldMessagesOfTheTicket
      .map((item) => parseMessageFromReassignedTicket(item));

    return parsedMessages;
  } catch (error) {
    new Greenmile.Components
      .AlertModal('An error occurred while trying to get the ticket old messages')
      .show();

    return [];
  }
}

function* openNewChatAndPutMessages(payload) {
  const {
    userName,
    ticketTitle,
    userId,
    ticketId
  } = payload;

  const oldMessagesOfTheTicket = yield call(getTicketOldMessages, ticketId);

  const newChat = {
    name: ticketTitle,
    fromName: userName,
    ticket: payload,
    from: userId,
    messages: oldMessagesOfTheTicket
  };

  yield put({ type: addNewChatWindow, payload: newChat });
}

function* updateTicketInHubState(ticketId) {
  const { user } = yield select();
  const userLoggedId = user.get('id');
  const userLoggedName = user.get('name');

  yield put({
    type: acceptTicketInTicketHub,
    payload: {
      ticketId,
      userLogged: {
        id: userLoggedId,
        name: userLoggedName
      }
    }
  });
}

function* submitAcceptCallSaga(action) {
  const { payload } = action;
  const ticketId = payload.get('ticketId');
  const isFromTicketHub = payload.get('fromTicketHub');

  const acceptTicketResponse = yield Monet.Session.Chat.Service.acceptTicket(ticketId);

  if (!hasRequestErrors(acceptTicketResponse?.message)) {
    const {
      userName,
      ticketTitle,
      userId
    } = acceptTicketResponse;

    const dataToOpenNewChat = {
      ...payload.toJS(),
      userName,
      ticketTitle,
      userId,
      ticketId
    };

    yield openNewChatAndPutMessages(dataToOpenNewChat);
    yield put({ 
      type: removeToast, 
      payload: { 
        id: ticketId 
      } 
    });

    isFromTicketHub
      ? yield updateTicketInHubState(ticketId) : null;
  } else {
    const defaultErrorMessage = Monet.I18n.getResourcesByKey('Chat.AcceptTicket.Error');
    const responseErrorMessage = acceptTicketResponse?.response?.data?.message;

    const errorMessage = responseErrorMessage || defaultErrorMessage;

    new Greenmile.Components
      .AlertModal(errorMessage)
      .show();
  }
}

function* watchSubmitAcceptCall() {
  yield takeLatest(submitAcceptCall, submitAcceptCallSaga);
}

function* watchSeeTicket() {
  yield takeLatest(seeTicketInWindow, ({ payload }) => openNewChatAndPutMessages(payload));
}

export {
  getTicketOldMessages
};

export default function* () {
  yield all([
    watchToggleFinishCallModal(),
    watchToggleReassignCallModal(),
    watchSubmitFinishCall(),
    watchSubmitReassignCall(),
    watchSubmitAcceptCall(),
    watchSeeTicket()
  ]);
}
